import React, { createContext, useContext, useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [modalView, setModalView] = useState('login'); // 'login' or 'register'
    const [user, setUser] = useState(pb.authStore.model);

    const openAuthModal = (view = 'login') => {
        setModalView(view);
        setIsAuthModalOpen(true);
    };

    const closeAuthModal = () => {
        setIsAuthModalOpen(false);
    };

    // 1. Sync User State and handle Real-time role updates
    useEffect(() => {
        let isMounted = true;
        let activeUserId = user?.id;

        const removeListener = pb.authStore.onChange((token, model) => {
            if (isMounted) {
                setUser(model);
                // If user changed (login/logout), we'll let the other useEffect handle subscription
            }
        }, true);

        return () => {
            isMounted = false;
            removeListener();
        };
    }, []);

    // 2. Separate Effect for Subscription to handle dynamic login/logout
    useEffect(() => {
        let isMounted = true;
        let subscribedId = null;

        const initSubscription = async () => {
            if (!user?.id || !pb.authStore.isValid) return;

            try {
                subscribedId = user.id;
                await pb.collection('users').subscribe(subscribedId, (e) => {
                    if (!isMounted) return;
                    if (e.action === 'update') {
                        const updatedUser = e.record;
                        const oldRole = pb.authStore.model?.role;

                        // Prevent infinite loop if update is from here
                        if (JSON.stringify(updatedUser) === JSON.stringify(pb.authStore.model)) return;

                        pb.authStore.save(pb.authStore.token, updatedUser);
                        setUser(updatedUser);

                        if (oldRole && updatedUser.role !== oldRole) {
                            toast.success(`🔐 Accès mis à jour : ${updatedUser.role.toUpperCase()}`);
                        }
                    }
                });
            } catch (err) {
                // Silently handle error 0 which is often just a transient network/cancel issue
                if (err.status !== 0) {
                    console.error("Auth subscription error:", err);
                }
            }
        };

        initSubscription();

        return () => {
            isMounted = false;
            if (subscribedId) {
                pb.collection('users').unsubscribe(subscribedId).catch(() => { });
            }
        };
    }, [user?.id]); // Re-subscribe whenever user changes

    return (
        <AuthContext.Provider value={{
            isAuthModalOpen,
            modalView,
            openAuthModal,
            closeAuthModal,
            setModalView,
            user,
            userRole: user?.role || 'client'
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
